import { ChangeDetectionStrategy, Component, effect, inject, input, InputSignal } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { ClientIdentificationResponse } from '@api/src/models/AgentApi/Contract/client-identification-response';
import { ListIdtypeXResponse } from '@api/src/models/AgentApi/Contract/list-idtype-x-response';
import { SellerSettings } from '@api/src/models/AgentApi/Contract/seller-settings';
import { Product } from '@core/calculator/product';
import { TranslatePipe } from '@ngx-translate/core';
import { preventDefault } from '@rx-angular/state/actions';
import { AbstractFormComponent } from '@shared/components/form/abstract-form/abstract-form.component';
import { FormButton } from '@shared/components/form/form-button/form-button';
import { FormCheckboxComponent } from '@shared/components/form/form-checkbox/form-checkbox.component';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { FormInputComponent } from '@shared/components/form/form-input/form-input.component';
import { FormSelectComponent } from '@shared/components/form/form-select/form-select.component';
import { SelectOption } from '@shared/components/form/form-select/select-option.model';
import { PriceBlock } from '@shared/components/price-block/price-block';
import { paymentTypes } from '@shared/data/payment-type';
import { MatDialogService } from '@shared/services/mat-dialog.service';
import { ClientSearch } from '../../../features/client-search/client-search';
import { AddCardFormField, AddCardFormService } from './add-card-form.service';

@Component({
    selector: 'app-add-card-form',
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        FormInputComponent,
        FormButton,
        FormErrorComponent,
        FormSelectComponent,
        FormCheckboxComponent,
        MatButton,
        PriceBlock,
    ],
    templateUrl: './add-card-form.html',
    styleUrl: './add-card-form.scss',
    providers: [AddCardFormService],
    host: {
        class: 'agent-add-card-form',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCardForm extends AbstractFormComponent {
    public readonly mediumTypes: InputSignal<SelectOption<ListIdtypeXResponse>[]> = input();
    public readonly client: InputSignal<ClientIdentificationResponse> = input();
    public readonly sellMode: InputSignal<boolean> = input();
    public readonly sellerSettings: InputSignal<SellerSettings> = input();

    readonly #addCardFormService: AddCardFormService = inject(AddCardFormService);
    readonly #dialog: MatDialogService = inject(MatDialogService);

    public override form: FormGroup = this.#addCardFormService.createReactiveForm();
    public readonly addCardFormField: typeof AddCardFormField = AddCardFormField;
    public readonly product: typeof Product = Product;
    public readonly paymentTypes: SelectOption[] = paymentTypes;

    constructor() {
        super();

        effect(() => {
            const client = this.client();
            const sellerSettings = this.sellerSettings();

            if (client) {
                this.#addCardFormService.getControlByPath(AddCardFormField.Mobnum).setValue(client.mobilePhone);
            }

            if (sellerSettings) {
                this.#addCardFormService
                    .getControlByPath(AddCardFormField.TopupSum)
                    .addValidators([Validators.min(sellerSettings.minTopup)]);
            }
        });

        this.#addCardFormService
            .watchForValueChanges(AddCardFormField.AddCardForClient)
            .subscribe((withRegister: boolean) => {
                if (withRegister && this.client()) {
                    this.#addCardFormService
                        .getControlByPath(AddCardFormField.Mobnum)
                        .setValue(this.client().mobilePhone);
                } else {
                    this.#addCardFormService.getControlByPath(AddCardFormField.Mobnum).setValue(null);
                }
            });
    }

    public get forExistingClient(): boolean {
        return this.#addCardFormService.getValueByPath(AddCardFormField.AddCardForClient);
    }

    public get topUpValue(): number {
        return Number(this.#addCardFormService.getValueByPath(AddCardFormField.TopupSum));
    }

    public get topUpControl(): AbstractControl {
        return this.#addCardFormService.getControlByPath(AddCardFormField.TopupSum);
    }

    public get mediumControlValue(): AbstractControl {
        return this.#addCardFormService.getControlByPath(AddCardFormField.MediumN);
    }

    public get mediumTypeControlValue(): AbstractControl {
        return this.#addCardFormService.getControlByPath(AddCardFormField.MediumType);
    }

    public override handlerSubmit: () => void = () => {
        this.handlers.handlerSubmit({
            mediumN: this.#addCardFormService.getValueByPath(AddCardFormField.MediumN),
            mediumType: this.#addCardFormService.getValueByPath(AddCardFormField.MediumType),
            termCode: this.#addCardFormService.getValueByPath(AddCardFormField.TermCode),
            paymentType: this.#addCardFormService.getValueByPath(AddCardFormField.PaymentType),
            topupSum: this.#addCardFormService.getValueByPath(AddCardFormField.TopupSum) || 0,
            mobnum:
                this.sellMode() && !this.forExistingClient
                    ? null
                    : this.#addCardFormService.getValueByPath(AddCardFormField.Mobnum) || null,
        });
    };

    public openDialogSearchClient(): void {
        this.#dialog.open(ClientSearch, {
            id: AddCardForm.name,
            width: '650px',
        });
    }

    protected readonly preventDefault = preventDefault;
}
